import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  createStore,
  createStoreFile,
  deleteStore,
  deleteStoreFile,
  getLocalStores,
  getStoreFiles,
  getStores,
  updateStore,
} from "../../../../services/api/instances/vectorStoreApi/methods";
import type {
  TStoreData,
  TStoreFile,
} from "../../../../services/api/instances/vectorStoreApi/types";
import {
  deleteFile,
  getFiles,
  uploadFile,
} from "../../../../services/api/methods";
import i18n from "../../../../services/i18n";
import type { RootState } from "../../../../services/redux/types";
import { setSelectedStore, setStoreAction } from "../slice";
import { getUpdatedStores } from "../../helpers";
import { getLocalStoreFilesAction } from "./local_store_actions";
import { getError } from "../../../../services/lib/getError";

export const getStoresAction = createAsyncThunk(
  "database/getStores",
  async (type: string, { rejectWithValue }) => {
    try {
      if (type === "both") {
        const [responseStores, responseLocalStores, responseFiles] =
          await Promise.allSettled([getStores(), getLocalStores(), getFiles()]);

        const stores: TStoreData[] =
          responseStores.status === "fulfilled"
            ? responseStores.value.data.map((store) => ({
                id: store.id,
                name: store.name,
                status: store.status,
                files: [],
                metadata: {
                  type: "openai",
                  internal_id:
                    store?.metadata &&
                    "internal_id" in store?.metadata &&
                    store.metadata.internal_id !== ""
                      ? store.metadata.internal_id
                      : "",
                },
              }))
            : [];
        const localStores: TStoreData[] =
          responseLocalStores.status === "fulfilled"
            ? responseLocalStores.value.data.map((store) => ({
                id: store.id,
                name: store.name,
                status: store.status,
                files: [],
                metadata: {
                  type: "local",
                  internal_id:
                    store?.metadata &&
                    "internal_id" in store?.metadata &&
                    store.metadata.internal_id !== ""
                      ? store.metadata.internal_id
                      : "",
                },
              }))
            : [];
        const files: TStoreFile[] =
          responseFiles.status === "fulfilled"
            ? responseFiles.value.data.map((file) => ({
                id: file.id,
                filename: file.filename,
                storeId: "",
              }))
            : [];

        const finalStores = [...stores, ...localStores];

        return { finalStores, files };
      } else if (type === "local") {
        const responseStores = await getLocalStores();

        const stores: TStoreData[] = responseStores.data.map((store) => ({
          id: store.id,
          name: store.name,
          status: store.status,
          files: [],
          metadata: {
            type: "local",
            internal_id: store.id,
          },
        }));
        return { finalStores: stores };
      } else {
        const [responseStores, responseFiles] = await Promise.allSettled([
          getStores(),
          getFiles(),
        ]);
        const stores: TStoreData[] =
          responseStores.status === "fulfilled"
            ? responseStores.value.data.map((store) => ({
                id: store.id,
                name: store.name,
                status: store.status,
                files: [],
                metadata: {
                  type: "openai",
                  internal_id:
                    store?.metadata &&
                    "internal_id" in store?.metadata &&
                    store.metadata.internal_id !== ""
                      ? store.metadata.internal_id
                      : "",
                },
              }))
            : [];
        const files: TStoreFile[] =
          responseFiles.status === "fulfilled"
            ? responseFiles.value.data.map((file) => ({
                id: file.id,
                filename: file.filename,
                storeId: "",
              }))
            : [];

        return { finalStores: stores, files };
      }
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
  {
    condition(_, { getState }) {
      const state = getState() as RootState;
      const { hasMutation } = state.databaseState;

      if (!hasMutation) return false;

      return true;
    },
  },
);

export const createStoreAction = createAsyncThunk(
  "database/createStore",
  async (name: string, ThunkAPI) => {
    try {
      const { dataStore } = (ThunkAPI.getState() as RootState).databaseState;
      const initialName =
        name === ""
          ? `${i18n.t("knowledge_base.default_name")} ${dataStore.length + 1}`
          : name;
      const postObj = {
        name: initialName,
        metadata: {
          type: "openai",
          internal_id: "",
        },
      };

      const store = await createStore(postObj);
      const newStore: TStoreData = {
        id: store.id,
        name: initialName,
        status: store.status,
        files: [],
        metadata: {
          type: "openai",
          internal_id: store.id,
        },
      };
      await updateStore(store.id, {
        name: initialName,
        metadata: {
          type: "openai",
          internal_id: store.id,
        },
      });
      return newStore;
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getFilesAction = createAsyncThunk(
  "database/getFiles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getFiles();

      const files: TStoreFile[] = response.data.map((file) => ({
        id: file.id,
        filename: file.filename,
        storeId: "",
      }));
      return files;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getStoreFilesAction = createAsyncThunk(
  "database/getStoreFiles",
  async (id: string, ThunkAPI) => {
    try {
      const { dataStore, fileList } = (ThunkAPI.getState() as RootState)
        .databaseState;
      const selectedStore = dataStore.find(
        (store: TStoreData) => store.id === id,
      );

      if (!selectedStore) return;

      const fileListMap = new Map(
        fileList.map((file: TStoreFile) => [file.id, file.filename]),
      );

      const response = await getStoreFiles(id as string);
      const updateStoreFiles = response.data.map((file) => {
        return {
          id: file.id,
          filename: fileListMap.get(file.id) ?? "",
          storeId: file.vector_store_id,
        };
      });

      const updates = getUpdatedStores(dataStore, selectedStore, {
        files: updateStoreFiles,
      });
      return updates;
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const createStoreFileAction = createAsyncThunk(
  "database/createStoreFiles",
  async (
    {
      file,
      storeId,
      tempFileId,
    }: { file: File; storeId: string; tempFileId: string },
    { rejectWithValue },
  ) => {
    try {
      const uploaded = await uploadFile({ file, purpose: "assistants" });
      const storeFile = await createStoreFile(storeId, uploaded.id);
      return {
        tempFileId,
        finalFile: {
          id: storeFile.id,
          filename: storeFile.filename ?? file.name,
          storeId: storeFile.vector_store_id,
          isLoading: false,
        },
        storeId,
      };
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const updateStoreAction = createAsyncThunk(
  "database/updateStore",
  async (storeName: string, ThunkAPI) => {
    try {
      const { selectedStore, dataStore } = (ThunkAPI.getState() as RootState)
        .databaseState;
      await updateStore(selectedStore?.id as string, {
        name: storeName,
      });
      if (!selectedStore) return;
      const updates = getUpdatedStores(dataStore, selectedStore, {
        name: storeName,
      });

      return updates;
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteStoreFileAction = createAsyncThunk(
  "database/deleteStoreFile",
  async (fileId: string, ThunkAPI) => {
    try {
      const { selectedStore, dataStore, fileList } = (
        ThunkAPI.getState() as RootState
      ).databaseState;

      if (!selectedStore) return;

      const updatedStoreFileList = selectedStore.files.filter(
        (file: TStoreFile) => file.id !== fileId,
      );
      const updates = getUpdatedStores(dataStore, selectedStore, {
        files: updatedStoreFileList,
      });
      const newFileList = fileList.filter(
        (file) => file.id !== fileId && file.storeId !== selectedStore.id,
      );

      await Promise.all([
        deleteFile(fileId),
        deleteStoreFile(selectedStore?.id, fileId),
      ]);

      return { updates, newFileList };
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteStoreAction = createAsyncThunk(
  "database/deleteStore",
  async (id: string, ThunkAPI) => {
    try {
      const { dataStore, selectedStore } = (ThunkAPI.getState() as RootState)
        .databaseState;
      if (!selectedStore) return;
      const updatedStores = dataStore.filter(
        (store: TStoreData) => store.id !== id,
      );
      const nextStore = updatedStores[0];

      ThunkAPI.dispatch(setStoreAction(updatedStores));

      if (!updatedStores[0]) {
        ThunkAPI.dispatch(setSelectedStore(null));
      } else {
        ThunkAPI.dispatch(setSelectedStore(nextStore as TStoreData));
        if (nextStore.metadata.type === "local") {
          await ThunkAPI.dispatch(
            getLocalStoreFilesAction(nextStore?.id as string),
          );
        } else {
          await ThunkAPI.dispatch(getStoreFilesAction(nextStore?.id as string));
        }
      }

      const deleteFilePromises = selectedStore?.files
        ? selectedStore?.files?.flatMap((file) => [
            deleteFile(file.id),
            deleteStoreFile(id, file.id),
          ])
        : [];
      await Promise.all(deleteFilePromises);
      await deleteStore(id);
      return;
    } catch (e: unknown) {
      const message = getError(e);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
